import React, { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, User, Building2, Calculator, Briefcase } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

// shadcn/ui components
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { useToast } from '../../hooks/use-toast';

// Store
import { useAuthStore } from '../../stores/authStore';

// User type options
const USER_TYPES = [
  { value: 'business_owner', label: 'Business Owner', icon: Building2, description: 'I run a business and need accounting help' },
  { value: 'ca_firm', label: 'CA/Accounting Firm', icon: Calculator, description: 'I provide accounting services to clients' },
  { value: 'cfo_finance', label: 'CFO/Finance Head', icon: Briefcase, description: 'I manage finance for an organization' },
] as const;

type UserType = typeof USER_TYPES[number]['value'];

// Zod validation schema
const waitlistSignupSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Full name must be at least 2 characters')
    .max(50, 'Full name must be less than 50 characters')
    .regex(/^[a-zA-Z\s]+$/, 'Full name can only contain letters and spaces'),
  email: z
    .string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required'),
  userType: z
    .enum(['business_owner', 'ca_firm', 'cfo_finance'], {
      message: 'Please select your role',
    }),
  acceptTerms: z
    .boolean()
    .refine((val) => val === true, 'You must accept the Terms of Service to continue')
});

type WaitlistSignupFormData = z.infer<typeof waitlistSignupSchema>;

const WaitlistSignup: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { waitlistSignup, isLoading, error, clearError } = useAuthStore();

  // Get pre-filled values from URL
  const preSelectedType = searchParams.get('type') as UserType | null;
  const preFilledEmail = searchParams.get('email') || '';

  const form = useForm<WaitlistSignupFormData>({
    resolver: zodResolver(waitlistSignupSchema),
    defaultValues: {
      fullName: '',
      email: preFilledEmail,  // Pre-fill email from login redirect
      userType: preSelectedType && USER_TYPES.some(t => t.value === preSelectedType)
        ? preSelectedType
        : undefined,
      acceptTerms: false
    },
    mode: 'onChange'
  });

  // Clear auth errors when component mounts or form changes
  useEffect(() => {
    clearError();
  }, [clearError]);

  const onSubmit = async (data: WaitlistSignupFormData) => {
    try {
      clearError();

      await waitlistSignup({
        fullName: data.fullName,
        email: data.email,
        userType: data.userType,
        acceptTerms: data.acceptTerms
      });

      // Navigate immediately after API success
      navigate(`/waitlist-confirmation?email=${encodeURIComponent(data.email)}`);
      return; // Don't execute anything else
    } catch (error) {
      toast({
        title: "Signup failed",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="flex justify-center mb-4">
            <img
              src="/logo-long.png"
              alt="AccountsGenie"
              className="h-12 object-contain cursor-pointer hover:opacity-80 transition-opacity"
            />
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Join the Waitlist</h1>
          <p className="text-gray-600">Be among the first to experience AccountsGenie</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          {/* Auth Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Full Name Field */}
              <FormField
                control={form.control}
                name="fullName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-gray-700">Full name</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter your full name"
                          className="pl-10 py-3 h-12 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Email Field */}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-gray-700">Email address</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-5 w-5 text-gray-400" />
                        </div>
                        <Input
                          {...field}
                          type="email"
                          placeholder="Enter your email"
                          className="pl-10 py-3 h-12 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* User Type Radio Buttons */}
              <FormField
                control={form.control}
                name="userType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-gray-700">I am a...</FormLabel>
                    <FormControl>
                      <div className="space-y-3 mt-2">
                        {USER_TYPES.map((type) => {
                          const Icon = type.icon;
                          const isSelected = field.value === type.value;
                          return (
                            <label
                              key={type.value}
                              className={`flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-50'
                                  : 'border-gray-200 hover:border-gray-300 bg-gray-50 hover:bg-gray-100'
                              }`}
                            >
                              <input
                                type="radio"
                                className="sr-only"
                                value={type.value}
                                checked={isSelected}
                                onChange={() => field.onChange(type.value)}
                              />
                              <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                                isSelected ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                              }`}>
                                <Icon className="h-5 w-5" />
                              </div>
                              <div className="ml-4 flex-1">
                                <p className={`text-sm font-semibold ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                  {type.label}
                                </p>
                                <p className={`text-xs ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
                                  {type.description}
                                </p>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                              }`}>
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-white" />
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Terms of Service Checkbox */}
              <FormField
                control={form.control}
                name="acceptTerms"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-start space-x-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="mt-1"
                        />
                      </FormControl>
                      <div className="grid gap-1.5 leading-none">
                        <FormLabel className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                          I agree to the{' '}
                          <Link to="/terms" className="text-blue-600 hover:text-blue-500 font-semibold">
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link to="/privacy" className="text-blue-600 hover:text-blue-500 font-semibold">
                            Privacy Policy
                          </Link>
                        </FormLabel>
                      </div>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 h-12 rounded-xl shadow-sm text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Joining waitlist...
                  </>
                ) : (
                  'Join the Waitlist'
                )}
              </Button>
            </form>
          </Form>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-blue-600 hover:text-blue-500 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistSignup;
